/**
 * AvailabilityGateBoard — Nexa Config → Availability Gate, as ONE page for every zone (G1/G2,
 * Jeff, 4–5 October 2026).
 *
 * Jeff: "these tabs need to be integrated into a single page that will support zone filtering,
 * similar to the Customer page and filter pattern." The per-zone tabs showed the platform-wide
 * switch on EVERY zone's tab (it looked like a per-zone control) plus an "other zones" panel, and
 * nothing showed what the public would actually meet — which is how opening the platform switch
 * to test the US silently opened Australia too (G2, 4 October 2026).
 *
 * The page now:
 *   · TOP ROW — the platform kill-switch, labelled for what it is: an EMERGENCY STOP. Closed, the
 *     gate is closed in every zone; open, each zone follows its own switch.
 *   · ONE TABLE — a row per zone × gate: the zone's own switch, the EFFECTIVE state (platform AND
 *     zone — `lib/gate-board.ts`, the edge's own rule), who changed it, when, and why, with the
 *     toggle on the row. The zone filter (the Customers page's `ZoneFilter`) NARROWS rows; it
 *     never targets a write — every write names the row's own zone.
 *   · EVERY TOGGLE confirms in the existing `ActionConfirmModal`, naming the exact zone and gate
 *     and the effective result. The platform switch's confirmation LISTS every zone whose effective
 *     state would change and must be explicitly confirmed — opening it can never silently open a
 *     zone again.
 *
 * Gate SEMANTICS are unchanged (ADR-124 D6/I8): the same reads and writes as before, the same KV
 * keys. Errors toast (the staff error standard); a failed read is shown as unknown and counted as
 * CLOSED, never open.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getSowGate,
  setSowGate,
  getBetaGate,
  setBetaGate,
  getKillSwitch,
  setKillSwitch,
  type SowGateState,
} from '../../lib/nexa-config-api';
import { HOME_ZONE, ZONES, type PortalZone } from '../../lib/zones';
import { ZoneCell, ZoneFilter, matchesZone } from '../../components/ZoneIndicators';
import { useZoneReadReport } from '../../components/ZoneContext';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { useEffectiveTimeZone, formatInstant } from '../../lib/time-format';
import { ActionConfirmModal } from './ActionConfirmModal';
import {
  GATE_LABEL,
  effectiveOpen,
  isOpenRead,
  zoneToggleOutcome,
  zonesChangedByPlatform,
  type GateKind,
  type GateRead,
  type ZoneGateRow,
} from '../../lib/gate-board';
import { ActionIcon } from '../../components/ActionIcon';

const GATES: GateKind[] = ['sow', 'beta'];
const READ_ZONE: Record<GateKind, (z: PortalZone) => Promise<SowGateState>> = { sow: getSowGate, beta: getBetaGate };
const WRITE_ZONE: Record<GateKind, (m: 'open' | 'closed', r: string, z: PortalZone) => Promise<unknown>> = {
  sow: setSowGate,
  beta: setBetaGate,
};

/** The emergency-stop sentence, verbatim from the brief, per gate. */
const EMERGENCY_STOP: Record<GateKind, string> = {
  sow: 'Emergency stop. While closed, sign-up is closed in every zone. While open, each zone follows its own switch below.',
  beta: 'Emergency stop. While closed, beta invites are closed in every zone. While open, each zone follows its own switch below.',
};

type Pending =
  | { kind: 'zone'; row: ZoneGateRow; target: 'open' | 'closed' }
  | { kind: 'platform'; gate: GateKind; target: 'open' | 'closed' };

function Pill({ open, unknown }: { open: boolean; unknown?: boolean }) {
  const cls = unknown ? 'gate-pill unknown' : open ? 'gate-pill open' : 'gate-pill closed';
  return <span className={cls}>{unknown ? 'UNKNOWN' : open ? 'OPEN' : 'CLOSED'}</span>;
}

export function AvailabilityGateBoard({ canToggle }: { canToggle: boolean }) {
  const { push } = useToast();
  const tz = useEffectiveTimeZone();
  const [platform, setPlatform] = useState<Record<GateKind, GateRead>>({ sow: null, beta: null });
  const [own, setOwn] = useState<Record<string, GateRead>>({});
  const [zoneFilter, setZoneFilter] = useState('all');
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const key = (zone: string, gate: GateKind) => `${zone}:${gate}`;

  const load = useCallback(async () => {
    const read = (p: Promise<SowGateState>, label: string): Promise<GateRead> =>
      p.then((s) => s, (e) => {
        console.error(`nexa-config: ${label} read failed`, e);
        return 'error' as const;
      });
    const [sowP, betaP, ...zoneReads] = await Promise.all([
      read(getKillSwitch('sow'), 'platform sow'),
      read(getKillSwitch('beta'), 'platform beta'),
      ...ZONES.flatMap((z) => GATES.map((g) => read(READ_ZONE[g](z), `${z.code} ${g}`))),
    ]);
    setPlatform({ sow: sowP, beta: betaP });
    const next: Record<string, GateRead> = {};
    let i = 0;
    for (const z of ZONES) for (const g of GATES) next[key(z.code, g)] = zoneReads[i++] ?? 'error';
    setOwn(next);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows: ZoneGateRow[] = useMemo(
    () => ZONES.flatMap((z) => GATES.map((g) => ({
      zone: z.code, jurisdiction: z.jurisdiction, gate: g, own: own[key(z.code, g)] ?? null,
    }))),
    [own],
  );
  const shown = rows.filter((r) => matchesZone(r, zoneFilter));
  const failedZones = [...new Set(rows.filter((r) => r.own === 'error').map((r) => r.zone))];
  // ADR-123 Decision 1 — the navigation bar names the zones on screen, as Customers does.
  useZoneReadReport({ rows: Object.keys(own).length ? rows : null, failedZones, filter: zoneFilter, scope: 'gates' });

  function openConfirm(p: Pending) {
    setPending(p);
    setReason('');
    setAcknowledged(false);
  }

  const affected = pending?.kind === 'platform'
    ? zonesChangedByPlatform(rows, pending.gate, platform[pending.gate], pending.target)
    : [];

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'platform') {
        await setKillSwitch(pending.gate, pending.target, reason.trim());
      } else {
        const zone = ZONES.find((z) => z.code === pending.row.zone)!;
        await WRITE_ZONE[pending.row.gate](pending.target, reason.trim(), zone);
      }
      push({ variant: 'success', title: 'Gate updated', detail: 'Re-read from the edge below.' });
      setPending(null);
      // Re-read rather than trusting what the client believes it wrote.
      await load();
    } catch (e) {
      const t = toastFromError(e, 'The gate was not changed');
      push({ variant: 'error', title: t.title, detail: t.detail ?? 'Please retry.' });
    } finally {
      setBusy(false);
    }
  }

  const changedLine = (r: GateRead): string => {
    if (r === null || r === 'error') return '—';
    if (r.source === 'unset') return 'Never set — closed by default';
    return r.updatedAt ? formatInstant(r.updatedAt, tz, 'datetime').text : '—';
  };

  // Every read has answered (or failed) — the toggles are live. Tests and the UAT harness wait on this.
  const allRead = platform.sow !== null && platform.beta !== null && rows.every((r) => r.own !== null);

  return (
    <div className="gate-board" data-testid="gate-board" data-loaded={allRead ? 'true' : 'false'} aria-busy={!allRead}>
      {/* ── TOP ROW — the platform kill-switch, named for what it is ── */}
      <section aria-label="Platform emergency stop" data-testid="gate-platform">
        <h2 className="ncfg-k">Platform emergency stop (all zones)</h2>
        <table className="portal-users-table gate-table">
          <thead>
            <tr><th scope="col">Gate</th><th scope="col">Emergency stop</th><th scope="col">What it means</th><th scope="col">Changed</th><th scope="col">By</th><th scope="col">Why</th><th scope="col">Actions</th></tr>
          </thead>
          <tbody>
            {GATES.map((g) => {
              const r = platform[g];
              const open = isOpenRead(r);
              return (
                <tr key={g} data-testid={`gate-platform-${g}`}>
                  <th scope="row">{GATE_LABEL[g]}</th>
                  <td><Pill open={open} unknown={r === 'error'} /></td>
                  <td className="gate-explain">{EMERGENCY_STOP[g]}</td>
                  <td>{changedLine(r)}</td>
                  <td>{r && r !== 'error' ? r.updatedBy ?? '—' : '—'}</td>
                  <td>{r && r !== 'error' ? r.reason ?? '—' : '—'}</td>
                  <td>
                    <ActionIcon act={open ? 'pause' : 'resume'} icon={open ? 'lock' : 'unlock'}
                      label={open ? 'Close everywhere' : 'Release emergency stop'}
                      tip={canToggle
                        ? (open ? `Close everywhere — the emergency stop for ${GATE_LABEL[g]}; asks you to confirm` : `Release the emergency stop for ${GATE_LABEL[g]}; asks you to confirm`)
                        : 'super_admin or nexa_admin only'}
                      disabled={!canToggle || r === null || r === 'error'}
                      onClick={() => openConfirm({ kind: 'platform', gate: g, target: open ? 'closed' : 'open' })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* ── ONE TABLE — every zone × gate ── */}
      <section aria-label="Zone gates" data-testid="gate-zones">
        <div className="portal-users-filters">
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
        </div>
        <table className="portal-users-table gate-table">
          <thead>
            <tr>
              <th scope="col">Zone</th><th scope="col">Gate</th><th scope="col">Zone switch</th>
              <th scope="col">Effective (what the public meets)</th>
              <th scope="col">Changed</th><th scope="col">By</th><th scope="col">Why</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const open = isOpenRead(r.own);
              const eff = effectiveOpen(platform[r.gate], r.own);
              const st = r.own && r.own !== 'error' ? r.own : null;
              return (
                <tr key={`${r.zone}-${r.gate}`} data-testid={`gate-row-${r.zone}-${r.gate}`}>
                  <td><ZoneCell zone={r.zone} /></td>
                  <td>{GATE_LABEL[r.gate]}</td>
                  <td>{r.own === null ? 'Loading…' : <Pill open={open} unknown={r.own === 'error'} />}</td>
                  <td data-testid={`gate-effective-${r.zone}-${r.gate}`}>
                    <Pill open={eff} />
                    {open && !eff && <span className="gate-note"> held by emergency stop</span>}
                  </td>
                  <td>{changedLine(r.own)}</td>
                  <td>{st?.updatedBy ?? '—'}</td>
                  <td>
                    {st?.closedBy === 'agent' && !open ? <span className="mon-err">Closed by the Security agent. </span> : null}
                    {st?.reason ?? '—'}
                  </td>
                  <td>
                    <ActionIcon act={open ? 'pause' : 'resume'} icon={open ? 'lock' : 'unlock'}
                      label={`${open ? 'Close' : 'Open'} ${GATE_LABEL[r.gate]} in ${r.jurisdiction}`}
                      tip={canToggle
                        ? `${open ? 'Close' : 'Open'} ${GATE_LABEL[r.gate]} in ${r.jurisdiction} — asks you to confirm`
                        : 'super_admin or nexa_admin only'}
                      disabled={!canToggle || r.own === null || r.own === 'error'}
                      onClick={() => openConfirm({ kind: 'zone', row: r, target: open ? 'closed' : 'open' })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="ncfg-desc">
          The two gates are independent: closing public sign-up never stops beta invites, or the
          reverse. Staff can always run SOW from the console — no gate affects it.{' '}
          <span title={`Home zone: ${HOME_ZONE.jurisdiction}`}>A failed read is shown as UNKNOWN and treated as closed.</span>
        </p>
      </section>

      {pending && (
        <ActionConfirmModal
          open
          title={pending.kind === 'platform'
            ? `${pending.target === 'open' ? 'Release' : 'Apply'} the ${GATE_LABEL[pending.gate].toLowerCase()} emergency stop`
            : `${pending.target === 'open' ? 'Open' : 'Close'} ${GATE_LABEL[pending.row.gate].toLowerCase()} in ${pending.row.jurisdiction}`}
          intent={pending.target === 'open' ? 'warning' : 'danger'}
          primaryLabel={pending.target === 'open' ? 'Confirm — open' : 'Confirm — close'}
          primaryDisabled={!reason.trim() || (pending.kind === 'platform' && affected.length > 0 && !acknowledged)}
          loading={busy}
          onConfirm={() => void confirm()}
          onCancel={() => setPending(null)}
        >
          {pending.kind === 'zone' ? (
            <p data-testid="gate-confirm-outcome">
              <strong>{zoneToggleOutcome(pending.row, platform[pending.row.gate], pending.target).sentence}</strong>
              {' '}Only {pending.row.jurisdiction} changes.
            </p>
          ) : (
            <>
              <p>{EMERGENCY_STOP[pending.gate]}</p>
              {affected.length === 0 ? (
                <p data-testid="gate-confirm-affected-none">
                  No zone’s effective state changes: {pending.target === 'open'
                    ? 'every zone’s own switch is closed, so every zone stays CLOSED.'
                    : 'no zone is open right now.'}
                </p>
              ) : (
                <>
                  <p><strong>This changes what the public meets in:</strong></p>
                  <ul data-testid="gate-confirm-affected">
                    {affected.map((a) => (
                      <li key={a.zone}>
                        <strong>{GATE_LABEL[pending.gate]} in {a.jurisdiction}</strong> will be <strong>{a.to.toUpperCase()}</strong>
                      </li>
                    ))}
                  </ul>
                  <label className="gate-ack">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      data-testid="gate-confirm-ack"
                    />{' '}
                    I confirm {affected.length === 1 ? 'this zone' : `all ${affected.length} zones`} above will change.
                  </label>
                </>
              )}
            </>
          )}
          <label className="gate-reason">
            Reason (required — audit trail)
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this changing?" />
          </label>
        </ActionConfirmModal>
      )}
    </div>
  );
}
